import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { ResultGrid } from '../../../../../grid/ResultGrid';
import { Column } from '../../../../../grid/Column';
import { DeleteIcon } from '../../../../../icon/DeleteIcon';
import { EditIcon } from '../../../../../icon/EditIcon';
import { ExternalLinkIcon } from '../../../../../table/ExternalLinkIcon';
import { InfoIcon } from '../../../../../icon/InfoIcon';
import {
  parseAndShortenFloat,
} from '../../../../../../helperFunctions/helperFunctions';
import {
  bulkEditRunups as bulkEditRunupsAc,
  cancelEdit as cancelEditAc,
  cancelEditConfirmation as cancelEditConfirmationAc,
  openEditConfirmation as openEditConfirmationAc,
  startEdit as startEditAc,
} from '../../../../../../redux/tsunamis/runups/data/runupDataActions';
import { createInitialForm, formToJson } from '../edit/runupEditModel';
import { checkboxCell, textCell } from '../../../../../grid/resultGridCells';
import { TextEditCell } from '../../../../../grid/TextEditCell';
import {
  minColumns,
  sort as defaultSort,
  visibleColumns as defaultColumns,
} from '../../../../../../redux/tsunamis/event/related-runup/runupReduxConstants';

export const latLonCell = (row, field, value, errors, editing) => {
  if (editing) {
    return (<TextEditCell errors={errors} name={`items[${row.get('id')}].${field}`} />);
  }
  return (<div>{parseAndShortenFloat(value, 5)}</div>);
};

const renderEditLink = id => (
  <Link aria-label={`Edit Runup ${id}`} to={`/view/hazards/tsunami/update-runup/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const renderTsunamiLink = id => (
  <Link aria-label={`Tsunami Event ${id}`} to={`/view/hazards/tsunami/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Tsunami Event" tooltip="View Related Tsunami Event" />
  </Link>
);

const renderEarthquakeLink = (id, content) => (
  <Link aria-label={`Earthquake Event ${id}`} to={`/view/hazards/earthquake/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    {content}
  </Link>
);

const renderVolcanoLink = id => (
  <Link aria-label={`Volcano Event ${id}`} to={`/view/hazards/volcano/event-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <ExternalLinkIcon ariaLabel="Link To Volcanic Event" tooltip="View Related Volcanic Event" />
  </Link>
);

const renderInfoLink = id => (
  <Link aria-label={`Runup Info ${id}`} to={`/view/hazards/tsunami/runup-more-info/${id}`} target="_blank" rel="noopener noreferrer">
    <InfoIcon />
  </Link>
);

const handleBeforeUnload = (event) => {
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = 'leaving reference';
};

class RunupDataGridUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.focus = this.focus.bind(this);
    this.handleYesClick = this.handleYesClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEditToggle = this.handleEditToggle.bind(this);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
    this.deleteCancel = null;
    this.state = {
      modal: false,
      selectedId: null,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      deleting,
      onDeleteCloseModal,
      saving,
      cancelEditConfirmation,
      errors,
      cancelEdit,
    } = this.props;
    if (!deleting && prevProps.deleting) {
      this.closeModal(() => {
        onDeleteCloseModal();
      });
    }
    if (!saving && prevProps.saving) {
      cancelEditConfirmation();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
      if (!errors.size) {
        cancelEdit();
        onDeleteCloseModal(); // reload
      }
    }
  }

  openModal(selectedId) {
    this.setState({
      modal: true,
      selectedId,
    });
  }

  closeModal(callback) {
    this.setState({
      modal: false,
      selectedId: null,
    }, callback);
  }

  handleDeleteClick(id) {
    this.openModal(id);
  }

  focus() {
    this.deleteCancel.focus();
  }

  handleYesClick() {
    const { deleteRunup } = this.props;
    const { selectedId } = this.state;
    deleteRunup({ id: selectedId });
  }

  handleSubmit(data) {
    const { bulkEditRunups } = this.props;
    bulkEditRunups({ data });
  }

  handleEditToggle(edit) {
    const { startEdit, cancelEdit, runups } = this.props;
    if (edit) {
      const items = {};
      const initialValues = { items };
      runups.forEach((item) => {
        items[`${item.get('id')}`] = createInitialForm(item).toJS();
      });
      startEdit({ initialValues });
      window.addEventListener('beforeunload', handleBeforeUnload, true);
    } else {
      cancelEdit();
      window.removeEventListener('beforeunload', handleBeforeUnload, true);
    }
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortData, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortData(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      runups,
      dataManager,
      sort,
      visibleColumns,
      searchParams,
      deleting,
      editConfirmationOpen,
      saving,
      formData,
      cancelEditConfirmation,
      openEditConfirmation,
      initialFormData,
      initialValues,
      editing,
      errors,
    } = this.props;

    const { modal } = this.state;

    return (
      <div className="haz-data-grid-container">

        <Modal isOpen={modal || deleting} onOpened={this.focus} backdrop="static">
          <ModalHeader>Delete Runup</ModalHeader>
          <ModalBody>
            {deleting ? 'Deleting...' : 'Are you sure you want to delete the runup?'}
          </ModalBody>
          <ModalFooter>
            <Button disabled={deleting} color="danger" onClick={this.handleYesClick}>Delete</Button>
            {' '}
            <Button disabled={deleting} color="secondary" innerRef={(ref) => { this.deleteCancel = ref; }} onClick={() => this.closeModal()}>Cancel</Button>
          </ModalFooter>
        </Modal>

        <ResultGrid
          data={runups}
          sort={sort}
          columnChooser
          visibleColumns={visibleColumns}
          handleColumnsToggle={this.handleToggleColumns}
          download
          downloadPrefix="runups"
          downloadSearchParams={searchParams}
          edit={dataManager}
          editing={editing}
          handleEditToggle={this.handleEditToggle}
          initialValues={initialValues}
          handleSubmit={this.handleSubmit}
          editConfirmationOpen={editConfirmationOpen}
          hazardName="Runups"
          saving={saving}
          handleCancel={cancelEditConfirmation}
          formData={formData}
          initialFormData={initialFormData}
          openEditConfirmation={openEditConfirmation}
          formToJson={formToJson}
          defaultColumns={defaultColumns}
          defaultSort={defaultSort}
          minColumns={minColumns}
        >
          {dataManager && (
            <Column alwaysVisible id="del" cell={({ row }) => (<DeleteIcon handleClick={() => this.handleDeleteClick(row.get('id'))} />)} width={30}>
              <DeleteIcon />
            </Column>
          )}
          {dataManager && (
            <Column alwaysVisible id="edit" cell={({ row }) => renderEditLink(row.get('id'))} width={30}>
              <EditIcon />
            </Column>
          )}
          <Column alwaysVisible={dataManager} id="id" width={50} sortable>Id</Column>
          {dataManager && (
            <Column alwaysVisible id="publish" cell={({ value, row }) => checkboxCell(row, 'publish', value, errors, editing)} width={45} sortable>Publish</Column>
          )}
          <Column id="year" width={45} sortable helpModalId="year">Year</Column>
          <Column id="month" width={35} sortable helpModalId="month">Mo</Column>
          <Column id="day" width={35} sortable helpModalId="day">Dy</Column>
          <Column id="hour" width={35} sortable helpModalId="hour">Hr</Column>
          <Column id="minute" width={35} sortable helpModalId="minute">Mn</Column>
          <Column id="second" width={35} sortable helpModalId="second">Sec</Column>
          <Column id="sourceEventValidity" width={85} sortable helpModalId="tsuValidity" cell={({ value, row }) => textCell(row, 'sourceEventValidity', value, errors, editing, { warning: (value === -1) })}>Tsunami Event Validity</Column>
          <Column id="sourceCauseCode" width={80} sortable helpModalId="tsuCauseCode">Tsunami Cause Code</Column>
          <Column id="sourceOceanicTsunami" width={80} sortable helpModalId="tsuOceanicTsunami" cell={({ value, row }) => checkboxCell(row, 'sourceOceanicTsunami', value, errors, editing)}>Oceanic Tsunami</Column>
          <Column id="tsunamiEventId" width={50} helpModalId="tsuMoreInfo" cell={({ value }) => value && renderTsunamiLink(value)}>Tsu Src</Column>
          <Column
            id="sourceEqMagnitude"
            width={80}
            sortable
            helpModalId="eqMagnitude"
            cell={({ value, row }) => {
              const earthquakeEventId = row.get('earthquakeEventId');
              let content;
              if (earthquakeEventId) {
                if (value != null) {
                  content = renderEarthquakeLink(earthquakeEventId, value);
                } else {
                  content = renderEarthquakeLink(earthquakeEventId, (<ExternalLinkIcon ariaLabel="Link To Earthquake Event" tooltip="View Related Earthquake Event" />));
                }
              }
              return content;
            }}
          >
            Earthquake Magnitude
          </Column>
          <Column id="volcanoEventId" width={35} helpModalId="tsuVolcano" cell={({ value }) => value && renderVolcanoLink(value)}>Vol</Column>
          <Column id="moreInfo" width={40} helpModalId="ruMoreInfo" cell={({ row }) => renderInfoLink(row.get('id'))}>More Info</Column>
          <Column id="doubtful" width={65} sortable helpModalId="ruDoubtful" cell={({ value, row }) => textCell(row, 'doubtful', value, errors, editing, { warning: (value === 'y'), info: (value === 'm') })}>Doubtful Runup</Column>
          <Column id="regionCode" width={45} sortable helpModalId="tsuRegion" cell={({ value, row }) => textCell(row, 'regionCode', value, errors, editing)}>Region</Column>
          <Column id="country" width={150} sortable helpModalId="ruCountry" cell={({ value, row }) => textCell(row, 'country', value, errors, editing)}>Country</Column>
          <Column id="area" width={150} sortable helpModalId="ruArea" cell={({ value, row }) => textCell(row, 'area', value, errors, editing)}>Area</Column>
          <Column id="locationName" width={200} sortable helpModalId="ruLocation" cell={({ value, row }) => textCell(row, 'locationName', value, errors, editing)}>Location Name</Column>
          <Column id="latitude" width={100} sortable helpModalId="ruLatitude" cell={({ value, row }) => latLonCell(row, 'latitude', value, errors, editing)}>Latitude</Column>
          <Column id="longitude" width={100} sortable helpModalId="ruLongitude" cell={({ value, row }) => latLonCell(row, 'longitude', value, errors, editing)}>Longitude</Column>
          <Column id="distFromSource" width={90} sortable helpModalId="ruDistance" cell={({ value }) => (<div>{parseAndShortenFloat(value, 0)}</div>)}>Distance From Source (km)</Column>
          <Column id="arrDay" width={80} sortable helpModalId="ruArrDay" cell={({ value, row }) => textCell(row, 'arrDay', value, errors, editing)}>Initial Wave Arrival Dy</Column>
          <Column id="arrHour" width={80} sortable helpModalId="ruArrHour" cell={({ value, row }) => textCell(row, 'arrHour', value, errors, editing)}>Initial Wave Arrival Hr</Column>
          <Column id="arrMin" width={90} sortable helpModalId="ruArrMinute" cell={({ value, row }) => textCell(row, 'arrMin', value, errors, editing)}>Initial Wave Arrival Min</Column>
          <Column id="travHours" width={50} sortable helpModalId="ruWaveTravelHour" cell={({ value, row }) => textCell(row, 'travHours', value, errors, editing)}>Travel Hours</Column>
          <Column id="travMins" width={50} sortable helpModalId="ruWaveTravelMinute" cell={({ value, row }) => textCell(row, 'travMins', value, errors, editing)}>Travel Minutes</Column>
          <Column id="maxWaveArrDay" width={80} sortable helpModalId="ruMaxArrDay" cell={({ value, row }) => textCell(row, 'maxWaveArrDay', value, errors, editing)}>Max Wave Arrival Day</Column>
          <Column id="maxWaveArrHour" width={80} sortable helpModalId="ruMaxArrHour" cell={({ value, row }) => textCell(row, 'maxWaveArrHour', value, errors, editing)}>Max Wave Arrival Hr</Column>
          <Column id="maxWaveArrMin" width={80} sortable helpModalId="ruMaxArrMinute" cell={({ value, row }) => textCell(row, 'maxWaveArrMin', value, errors, editing)}>Max Wave Arrival Min</Column>
          <Column id="runupHt" width={100} sortable helpModalId="ruHeight" cell={({ value, row }) => textCell(row, 'runupHt', value, errors, editing)}>Max Water Height (m)</Column>
          {dataManager && (
          <Column id="maxTroughDay" width={80} sortable helpModalId="ruMaxTroughDay" cell={({ value, row }) => textCell(row, 'maxTroughDay', value, errors, editing)}>Max Trough Day</Column>
          )}
          {dataManager && (
            <Column id="maxTroughHour" width={80} sortable helpModalId="ruMaxTroughHour" cell={({ value, row }) => textCell(row, 'maxTroughHour', value, errors, editing)}>Max Trough Hr</Column>
          )}
          {dataManager && (
          <Column id="maxTroughMin" width={80} sortable helpModalId="ruMaxTroughMinute" cell={({ value, row }) => textCell(row, 'maxTroughMin', value, errors, editing)}>Max Trough Min</Column>
          )}
          {dataManager && (
          <Column id="maxTroughHt" width={100} sortable helpModalId="ruTroughHt" cell={({ value, row }) => textCell(row, 'maxTroughHt', value, errors, editing)}>Max Trough Height (m)</Column>
          )}
          <Column id="runupHoriz" width={100} sortable helpModalId="ruInundation" cell={({ value, row }) => textCell(row, 'runupHoriz', value, errors, editing)}>Max Inundation Distance (m)</Column>
          <Column id="typeMeasurementId" width={90} sortable helpModalId="ruMeasurementType" cell={({ value, row }) => textCell(row, 'typeMeasurementId', value, errors, editing)}>Measurement Type</Column>
          <Column id="period" width={50} sortable helpModalId="ruPeriod" cell={({ value, row }) => textCell(row, 'period', value, errors, editing)}>Period</Column>
          <Column id="firstMotion" width={50} sortable helpModalId="ruFirstMotion" cell={({ value, row }) => textCell(row, 'firstMotion', value, errors, editing)}>First Motion</Column>
          <Column id="deaths" width={50} sortable helpModalId="ruDeaths" cell={({ value, row }) => textCell(row, 'deaths', value, errors, editing)}>Deaths</Column>
          <Column id="deathsAmountOrder" width={80} sortable helpModalId="ruDeathsDescription" cell={({ value, row }) => textCell(row, 'deathsAmountOrder', value, errors, editing)}>Death Description</Column>
          <Column id="missing" width={50} sortable helpModalId="ruMissing" cell={({ value, row }) => textCell(row, 'missing', value, errors, editing)}>Missing</Column>
          <Column id="missingAmountOrder" width={80} sortable helpModalId="ruMissingDescription" cell={({ value, row }) => textCell(row, 'missingAmountOrder', value, errors, editing)}>Missing Description</Column>
          <Column id="injuries" width={50} sortable helpModalId="ruInjuries" cell={({ value, row }) => textCell(row, 'injuries', value, errors, editing)}>Injuries</Column>
          <Column id="injuriesAmountOrder" width={80} sortable helpModalId="ruInjuriesDescription" cell={({ value, row }) => textCell(row, 'injuriesAmountOrder', value, errors, editing)}>Injuries Description</Column>
          <Column id="damageMillionsDollars" width={50} sortable helpModalId="ruDamage" cell={({ value, row }) => textCell(row, 'damageMillionsDollars', value, errors, editing)}>Damage $Mil</Column>
          <Column id="damageAmountOrder" width={80} sortable helpModalId="ruDamageDescription" cell={({ value, row }) => textCell(row, 'damageAmountOrder', value, errors, editing)}>Damage Description</Column>
          <Column id="housesDestroyed" width={80} sortable helpModalId="ruHousesDestroyed" cell={({ value, row }) => textCell(row, 'housesDestroyed', value, errors, editing)}>Houses Destroyed</Column>
          <Column id="housesDestroyedAmountOrder" width={110} sortable helpModalId="ruHousesDestroyedDescription" cell={({ value, row }) => textCell(row, 'housesDestroyedAmountOrder', value, errors, editing)}>Houses Destroyed Description</Column>
          <Column id="housesDamaged" width={70} sortable helpModalId="ruHousesDamaged" cell={({ value, row }) => textCell(row, 'housesDamaged', value, errors, editing)}>Houses Damaged</Column>
          <Column id="housesDamagedAmountOrder" width={110} sortable helpModalId="ruHousesDamagedDescription" cell={({ value, row }) => textCell(row, 'housesDamagedAmountOrder', value, errors, editing)}>Houses Damaged Description</Column>
        </ResultGrid>
      </div>
    );
  }
}


RunupDataGridUnwrapped.propTypes = {
  runups: ImmutablePropTypes.list.isRequired,
  dataManager: PropTypes.bool.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  sortData: PropTypes.func.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  searchParams: PropTypes.string.isRequired,
  deleting: PropTypes.bool.isRequired,
  deleteRunup: PropTypes.func.isRequired,
  onDeleteCloseModal: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  bulkEditRunups: PropTypes.func.isRequired,
  editConfirmationOpen: PropTypes.bool.isRequired,
  formData: ImmutablePropTypes.map.isRequired,
  initialFormData: ImmutablePropTypes.map.isRequired,
  openEditConfirmation: PropTypes.func.isRequired,
  cancelEditConfirmation: PropTypes.func.isRequired,
  errors: ImmutablePropTypes.map.isRequired,
  startEdit: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  editing: PropTypes.bool.isRequired,
  initialValues: ImmutablePropTypes.map.isRequired,
};

const mapStateToProps = state => ({
  saving: state.getIn(['runupDataPage', 'saving']),
  editConfirmationOpen: state.getIn(['runupDataPage', 'editConfirmationOpen']),
  formData: state.getIn(['runupDataPage', 'formData']),
  initialFormData: state.getIn(['runupDataPage', 'initialFormData']),
  errors: state.getIn(['runupDataPage', 'errors']),
  editing: state.getIn(['runupDataPage', 'editing']),
  initialValues: state.getIn(['runupDataPage', 'initialValues']),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  bulkEditRunups: bulkEditRunupsAc,
  openEditConfirmation: openEditConfirmationAc,
  cancelEditConfirmation: cancelEditConfirmationAc,
  startEdit: startEditAc,
  cancelEdit: cancelEditAc,
}, dispatch);

export const RunupDataGrid = connect(mapStateToProps, mapDispatchToProps)(RunupDataGridUnwrapped);
